import { Box } from '@mui/material';
import type { ReactNode } from 'react';
import { NEOBET_COMPACT_DRAWER_WIDTH, NEOBET_DESKTOP_DRAWER_WIDTH } from '../constants';
import {
  getNeobetSidebarTransitionTiming,
  NEOBET_SIDEBAR_CONTENT_DELAY_MS,
  NEOBET_SIDEBAR_TRANSITION_MS,
} from '../sidebarMotion';

const expandedOffset = NEOBET_DESKTOP_DRAWER_WIDTH - NEOBET_COMPACT_DRAWER_WIDTH;

const getVisibilityTransition = (isVisible: boolean) =>
  `visibility 0s linear ${isVisible ? 0 : NEOBET_SIDEBAR_TRANSITION_MS}ms`;

const styles = {
  root: {
    position: 'relative',
    width: { xs: 1, md: NEOBET_DESKTOP_DRAWER_WIDTH },
    overflow: 'clip',
  },
  expanded: (isCompact: boolean) => {
    const transitionTiming = getNeobetSidebarTransitionTiming(isCompact);

    return {
      position: isCompact ? 'absolute' : 'relative',
      top: 0,
      left: 0,
      width: 1,
      opacity: isCompact ? 0 : 1,
      visibility: isCompact ? 'hidden' : 'visible',
      pointerEvents: isCompact ? 'none' : 'auto',
      transform: isCompact ? `translateX(-${expandedOffset}px)` : 'translateX(0)',
      transition: [
        `transform ${transitionTiming} ${NEOBET_SIDEBAR_CONTENT_DELAY_MS}ms`,
        `opacity ${transitionTiming}`,
        getVisibilityTransition(!isCompact),
      ].join(', '),
      willChange: 'transform, opacity',
    };
  },
  compact: (isCompact: boolean) => {
    const transitionTiming = getNeobetSidebarTransitionTiming(isCompact);

    return {
      position: isCompact ? 'relative' : 'absolute',
      top: 0,
      left: 0,
      width: NEOBET_COMPACT_DRAWER_WIDTH,
      opacity: isCompact ? 1 : 0,
      visibility: isCompact ? 'visible' : 'hidden',
      pointerEvents: isCompact ? 'auto' : 'none',
      transition: [`opacity ${transitionTiming}`, getVisibilityTransition(isCompact)].join(', '),
      willChange: 'opacity',
    };
  },
} as const;

type SidebarTransitionProps = {
  compactContent: ReactNode;
  expandedContent: ReactNode;
  isCompact: boolean;
};

const SidebarTransition = ({ compactContent, expandedContent, isCompact }: SidebarTransitionProps) => (
  <Box sx={styles.root} data-testid='neobet-betting-sidebar'>
    <Box sx={styles.expanded(isCompact)} aria-hidden={isCompact} data-testid='neobet-sidebar-expanded-presentation'>
      {expandedContent}
    </Box>
    <Box sx={styles.compact(isCompact)} aria-hidden={!isCompact} data-testid='neobet-sidebar-compact-presentation'>
      {compactContent}
    </Box>
  </Box>
);

export default SidebarTransition;
